import { useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import Copy from '@/assets/icons/Copy';
import ExternalLink from '@/assets/icons/ExternalLink';
import { Button } from '@/components/UI/Button/Button';
import ErrorState from '@/components/UI/ErrorState/ErrorState';

const CHUNK_LOAD_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'error loading dynamically imported module',
];

const hasMessage = (value: unknown): value is { message: string } =>
  typeof value === 'object' && value !== null && 'message' in value && typeof value.message === 'string';

const hasName = (value: unknown): value is { name: string } =>
  typeof value === 'object' && value !== null && 'name' in value && typeof value.name === 'string';

const getErrorMessage = (error: unknown) => {
  if (isRouteErrorResponse(error)) {
    if (typeof error.data === 'string') {
      return error.data;
    }

    if (hasMessage(error.data)) {
      return error.data.message;
    }

    return error.statusText;
  }

  if (hasMessage(error)) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  console.error(error);
  return 'Unknown error';
};

const isChunkLoadError = (error: unknown, errorMessage: string) => {
  const lowerCasedMessage = errorMessage.toLowerCase();
  const hasKnownChunkMessage = CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => lowerCasedMessage.includes(pattern));
  const hasChunkLoadErrorName = hasName(error) && error.name.toLowerCase().includes('chunkloaderror');

  return hasKnownChunkMessage || hasChunkLoadErrorName;
};

export function ErrorBoundary() {
  const error = useRouteError();
  const errorMessage = getErrorMessage(error);
  const showVersionMessage = isChunkLoadError(error, errorMessage);

  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard
      .writeText(errorMessage)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch((err) => {
        console.log('copy failed', err);
      });
  };

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '24px',
        backgroundColor: 'var(--tractie-color-background-elevated-sunken)',
      }}
    >
      <ErrorState
        title={showVersionMessage ? 'Er is mogelijk een nieuwe versie van de applicatie beschikbaar' : undefined}
        description={
          showVersionMessage
            ? 'Deze pagina kon niet goed geladen worden. Klik op "Pagina herladen" om de nieuwste versie op te halen.'
            : undefined
        }
      >
        <Button
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderRadius: '6px',
          }}
          onClick={() => window.location.reload()}
        >
          Pagina herladen
        </Button>

        <a
          href='https://forms.office.com/Pages/ResponsePage.aspx?id=WYFFZJoNhE2WbxoTwKx6NMy5veSVTdVBkL-jIhDQ4R1UQkNYUlJWTERLMEtLWThURTNLU1VRWTdIOC4u'
          target='_blank'
          rel='noreferrer'
          style={{ textDecoration: 'none', marginTop: '8px' }}
        >
          <Button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              border: 'none',
            }}
          >
            <ExternalLink width='24px' height='24px' />
            Neem contact op
          </Button>
        </a>
      </ErrorState>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingBottom: '16px',
          gap: '8px',
        }}
      >
        <h4
          style={{
            margin: 0,
            fontWeight: 400,
            color: 'var(--tractie-color-content-subtle)',
          }}
        >
          Foutmelding:
        </h4>

        <div
          style={{
            fontWeight: 700,
            color: 'var(--tractie-color-content-error)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {errorMessage}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Copy width='32px' height='32px' onClick={copyCode} style={{ cursor: 'pointer' }} />
          </div>
          {copied && (
            <span
              style={{
                fontSize: '12px',
                color: 'var(--tractie-color-content-success)',
              }}
            >
              Gekopieerd!
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
